import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
//import InvoiceForm from '../InvoiceFormx';
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Pagination, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Spinner } from 'reactstrap';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaCheckCircle, FaEdit, FaEllipsisH, FaFilePdf, FaFilter, FaInfo, FaPlus, FaSearch, FaShoppingBasket, FaTrash, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { format, parse } from 'date-fns';
import { purchaseActions } from '../../sagas/purchaseSlice';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import { PURCHASE_FORM_PAGE } from '../../routes/routeConstants';
import Select from "react-select";
import { supplierActions } from '../../sagas/supplierSlice';
import { productActions } from '../../sagas/productSlice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DocumentPagination from '../Common/DocumentPagination';
import '../Common/DocumentList.css';

const PurchasePage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { purchases, loading } = useSelector((state) => state.purchases);
    const { company } = useSelector(state => state.auth);
    const { t } = useTranslation();
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: '',
        page: 0,
        size: 10
    });

    const searchInputRef = useRef(null);

    useEffect(() => {
        if (suppliers.length === 0) {
            // Fetch suppliers for supplier report
            const fetchSuppliers = async () => {
                dispatch(supplierActions.search({
                    query: ""
                }));
            };

            fetchSuppliers();
        }
        dispatch(productActions.findAllUnites());
    }, []);

    useEffect(() => {
        let payload = {
            ...filters,
        };
        dispatch(purchaseActions.search(payload));
    }, [filters.fournisseurId, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        searchInputRef.current?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [purchases]);


    const debouncedSearch = () => {
        dispatch(purchaseActions.search({
            ...filters,
        }));
    }

    const refrechInvoice = () => {
        let payload = {
            ...filters,
        };
        dispatch(purchaseActions.search(payload));
    }

    const handleShowModal = () => {
        navigate(PURCHASE_FORM_PAGE);
    };

    const handleUpdateOrder = (purchase) => {
        navigate(PURCHASE_FORM_PAGE, { state: { purchase } });
    };


    const getStatusBadge = (status) => {
        switch (status) {
            case 'EN_ATTENTE':
                return <Badge color="secondary">En attente confirmation</Badge>;
            case 'VALIDE':
                return <Badge color="info">Commandé</Badge>;
            case 'LIVRE':
                return <Badge color="primary">Livré</Badge>;
            case 'ANNULE':
                return <Badge color="danger">Annulé</Badge>;
            default:
                return <Badge color="light">-</Badge>;
        }
    };


    if (loading) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}
            >
                <Spinner color="primary" />
            </div>
        );
    }

    const handleGeneratePdf = (
        bonCommande,
        {
            docType = "COMMANDE FOURNISSEUR",
            showPrices = true,
            priceMode = "TTC", // "HT" | "TTC"
            company = {},
        } = {}
    ) => {

        const doc = new jsPDF({ unit: "mm", format: "a4" });

        /* ================== CONSTANTS ================== */
        const PAGE_W = doc.internal.pageSize.width;
        const PAGE_H = doc.internal.pageSize.height;
        const M = 20;
        const FOOTER_H = 22;

        const BRAND = {
            primary: [6, 145, 43],
            ink: [15, 23, 42],
            subtle: [100, 116, 139],
            line: [226, 232, 240],
            headerBg: [241, 245, 249],
        };

        /* ================== HELPERS ================== */
        const fmtMoney = (v) =>
            Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        const amountToWords = (n) =>
            convertNumberToWords(Math.round(n || 0)).toUpperCase() + " DH";

        /* ================== HEADER ================== */
        const drawHeader = () => {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(22);
            doc.setTextColor(...BRAND.primary);
            doc.text(company.name || "MA SOCIÉTÉ", PAGE_W / 2, 22, { align: "center" });

            if (company.subtitle) {
                doc.setFontSize(10);
                doc.text(company.subtitle, PAGE_W / 2, 29, { align: "center" });
            }

            doc.setDrawColor(...BRAND.line);
            doc.line(M, 34, PAGE_W - M, 34);
        };

        /* ================== INFO CARDS ================== */
        const CARD_W = (PAGE_W - M * 3) / 2;
        const CARD_H = 45;
        const CARD_Y = 40;

        const drawCard = (x, title, lines) => {
            doc.setDrawColor(0);
            doc.roundedRect(x, CARD_Y, CARD_W, CARD_H, 3, 3);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...BRAND.ink);
            doc.text(String(title || ""), x + 5, CARD_Y + 8);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(...BRAND.subtle);

            (lines || []).forEach((l, i) => {
                if (l === null || l === undefined) return;

                // ✅ jsPDF-safe conversion
                const text =
                    typeof l === "string" ? l :
                        typeof l === "number" ? String(l) :
                            JSON.stringify(l);

                doc.text(text, x + 5, CARD_Y + 16 + i * 6);
            });
        };

        /* ================== TABLE ================== */
        const tableY = CARD_Y + CARD_H + 10;

        const headers = ["#", "Article", "Qté", "UM", "PU TTC", "Total TTC"]

        const rows = (bonCommande.lignes || []).map((l, i) =>
            [
                i + 1,
                l.articleNom,
                l.quantiteCommandee,
                l.unite,
                fmtMoney(priceMode === "HT" ? l.prixUnitaireHT : l.prixUnitaireTTC),
                fmtMoney(priceMode === "HT" ? l.montantHT : l.montantTTC),
            ]

        );

        /* ================== TOTALS ================== */
        const drawTotals = (y) => {
            const boxW = 70;
            const boxH = 52;
            const x = PAGE_W - M - boxW;
            const totalAvantRemise = bonCommande.montantTotalAvantRemise ?? bonCommande.montantTotalTTC;
            const montantRemise = bonCommande.montantRemise ?? 0;

            // === Background ===
            doc.setFillColor(163, 240, 183);
            doc.roundedRect(x, y, boxW, boxH, 3, 3, "F");

            doc.setTextColor(...BRAND.ink);

            // === Labels ===
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);

            doc.text("Total HT", x + 5, y + 10);
            doc.text("TVA (20%)", x + 5, y + 18);
            doc.text("Total avant remise", x + 5, y + 26);
            doc.text("Remise", x + 5, y + 34);

            doc.setFont("helvetica", "bold");
            doc.text("Total TTC", x + 5, y + 44);

            // === Values (right aligned) ===
            doc.setFont("helvetica", "normal");
            doc.text(fmtMoney(bonCommande.montantTotalHT), x + boxW - 5, y + 10, { align: "right" });
            doc.text(fmtMoney(bonCommande.montantTotalTaxe), x + boxW - 5, y + 18, { align: "right" });
            doc.text(fmtMoney(totalAvantRemise), x + boxW - 5, y + 26, { align: "right" });
            doc.text(fmtMoney(montantRemise), x + boxW - 5, y + 34, { align: "right" });

            doc.setFont("helvetica", "bold");
            doc.text(fmtMoney(bonCommande.montantTotalTTC), x + boxW - 5, y + 44, { align: "right" });
        };

        /* ================== FOOTER ================== */
        const drawFooter = (pageNo) => {
            doc.setDrawColor(0);
            doc.line(M, PAGE_H - FOOTER_H, PAGE_W - M, PAGE_H - FOOTER_H);
            doc.setFontSize(8);
            doc.setTextColor(80);
            doc.text(
                `${company.address || ""} • ${company.phone1 || ""} • ${company.email || ""}`,
                PAGE_W / 2,
                PAGE_H - 12,
                { align: "center" }
            );

            doc.text(
                `Page ${pageNo}`,
                PAGE_W - M,
                PAGE_H - 6,
                { align: "right" }
            );
        };

        const paymentLabel = (mode) => {
            switch (mode) {
                case "CARTE_BANCAIRE":
                    return "Carte bancaire";
                case "ESPECES":
                    return "Espèces";
                case "CHEQUE":
                    return "Chèque";
                case "VIREMENT":
                    return "Virement";
                case "TRAITE":
                    return "Traite";
                default:
                    return mode || "-";
            }
        };
        /* ================== BUILD DOCUMENT ================== */
        drawHeader();

        const lines = [
            `Numéro : ${bonCommande.numero || "-"}`,
            `Date Commande : ${bonCommande.dateCommande || "-"}`,
            `Mode paiement : ${paymentLabel(bonCommande.modeReglement)}`,
        ];

        // ➜ Afficher numéro de chèque uniquement si mode = CHEQUE
        if (bonCommande.modeReglement === "CHEQUE" && bonCommande.numeroCheque) {
            lines.push(`Numéro chèque : ${bonCommande.numeroCheque}`);
        }

        drawCard(M, docType, lines);

        drawCard(
            M * 2 + CARD_W,
            "FOURNISSEUR",
            [
                bonCommande.fournisseurNom,
                bonCommande.fournisseurVille,
                `Tél : ${bonCommande.fournisseurTelephone || "-"}`,
            ]
        );

        autoTable(doc, {
            startY: tableY,
            head: [headers],
            body: rows,
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: {
                fillColor: BRAND.headerBg,
                textColor: BRAND.ink,
                fontStyle: "bold",
            },
            didDrawPage: (data) => {
                drawFooter(doc.internal.getNumberOfPages());
            },
        });

        let yAfter = doc.lastAutoTable.finalY + 10;




        drawTotals(yAfter);
        doc.setFontSize(9);
        doc.setTextColor(...BRAND.subtle);
        doc.text(
            `Arrêté le présent document à la somme de : ${amountToWords(bonCommande.montantTotalTTC)}`,
            M,
            yAfter + 50
        );



        const pdfBlob = doc.output("bloburl");
        window.open(pdfBlob, "_blank");
    };
    const handleValiderBC = (purchase) => {
        Swal.fire({
            title: 'Confirmation',
            text: "Voulez-vous vraiment valider cette commande?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(purchaseActions.validerBC({
                    id: purchase.id,
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Validé!',
                            text: 'La commande a été validée.',
                            showConfirmButton: false,
                            timer: 1500
                        });
                        refrechInvoice();
                    }
                }));
            }
        });
    };

    const handleSupplierChange = (value) => {
        setFilters(f => ({ ...f, fournisseurId: value, page: 0 }));
    };

    const clearFilters = () => {
        setFilters({
            query: '',
            fournisseurId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate),
            page: 0,
            size: filters.size
        });
    };

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];
    const safePurchases = Array.isArray(purchases) ? purchases : [];
    const purchasePageNumber = Number(filters.page || 0);
    const purchasePageSize = Number(filters.size || 10);
    const paginatedPurchases = safePurchases.slice(
        purchasePageNumber * purchasePageSize,
        purchasePageNumber * purchasePageSize + purchasePageSize
    );
    const purchaseTotalPages = Math.ceil(safePurchases.length / purchasePageSize);


    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaShoppingBasket className="text-primary me-2" />
                                {t('purchase.orders')}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="mr-2" /> {t('purchase.newOrder')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-3">
	                                <Row className="mb-3 g-2 align-items-end doc-filter-row">
                                    <Col lg={3} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('commande.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
	                                                        setFilters(prevFilters => ({
	                                                            ...prevFilters,
	                                                            query: value,
	                                                            page: 0
	                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch(e.target.value.toUpperCase());
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label for="supplierSelect" className="form-label">
                                                <FaUser className="me-1" /> Fournisseur
                                            </Label>
                                            <Select
                                                id="supplierSelect"
                                                options={fournisseurOptions}
                                                isLoading={suppliersLoading}
                                                value={fournisseurOptions.find(option => option.value === filters.fournisseurId)}
                                                onChange={(option) => handleSupplierChange(option?.value)}
                                                placeholder="Sélectionner..."
                                                isClearable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
	                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, page: 0 }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
	                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, page: 0 }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
	                                </Row>
	                            </div>
	                            <DocumentPagination
	                                pageNumber={purchasePageNumber}
	                                totalPages={purchaseTotalPages}
	                                totalElements={safePurchases.length}
	                                pageSize={purchasePageSize}
	                                onPageChange={(page) => setFilters(f => ({ ...f, page }))}
	                            />
	                            <div className="doc-list">
	                                {paginatedPurchases?.map((purchase) => (
	                                    <div className="doc-row" key={purchase.id}>
	                                        <div className="doc-row-main">
	                                            <strong>{purchase.numero}</strong>
	                                            <span>{t('purchase.supplier')}: {purchase.fournisseurNom} ({purchase.fournisseurCode})</span>
	                                        </div>
	                                        <div className="doc-client">
	                                            <strong>{t('purchase.dateCreation')}: {purchase.createdAt || '-'}</strong>
	                                            <span>{t('purchase.dateModification')}: {purchase.updatedAt || 'N/A'}</span>
	                                            <span>{t('purchase.dateOperation')}: {purchase.dateOperation || 'N/A'}</span>
	                                        </div>
	                                        <div className="doc-amounts">
	                                            <div className="doc-amount">
	                                                <span>{t('purchase.totalAmountTtc')}</span>
	                                                <strong>{formatPrice(purchase.montantTotalTTC)}</strong>
	                                            </div>
	                                        </div>
	                                        <div className="doc-statuses">
	                                            {getStatusBadge(purchase.statut)}
	                                        </div>
	                                        <div className="doc-actions">
	                                            <Button
	                                                color="secondary"
	                                                title={t('commande.downloadPdf')}
	                                                onClick={() => handleGeneratePdf(purchase, {
	                                                    docType: "COMMANDE FOURNISSEUR",
	                                                    showPrices: true,
	                                                    priceMode: "TTC",
	                                                    company,
	                                                })}
	                                                className="doc-icon-btn"
	                                                size="sm"
	                                            >
	                                                <FaFilePdf />
	                                            </Button>
	                                            {purchase.statut === 'EN_ATTENTE' && (
	                                                <Button color="success" title={t('devis.devisValide')} onClick={() => handleValiderBC(purchase)} className="doc-icon-btn" size="sm">
	                                                    <FaCheckCircle />
	                                                </Button>
	                                            )}
	                                            <Button color="warning" title="Modifier" onClick={() => handleUpdateOrder(purchase)} size="sm" className="doc-icon-btn">
	                                                <FaEdit size={12} />
	                                            </Button>
	                                        </div>
	                                    </div>
	                                ))}
	                                {safePurchases.length === 0 && <div className="doc-empty">{t('purchase.noOrdersFound')}</div>}
	                                {paginatedPurchases.length > 0 && (
	                                    <div className="doc-row">
	                                        <div className="doc-row-main">
	                                            <strong>{t('common.totals')}</strong>
	                                            <span>{paginatedPurchases.length} lignes</span>
	                                        </div>
	                                        <div className="doc-client" />
	                                        <div className="doc-amounts">
	                                            <div className="doc-amount">
	                                                <span>{t('purchase.totalAmountTtc')}</span>
	                                                <strong>{paginatedPurchases.reduce((sum, p) => sum + (Number(p.montantTotalTTC) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
	                                            </div>
	                                        </div>
	                                        <div />
	                                        <div />
	                                    </div>
	                                )}
	                            </div>
                        </>)}
                </CardBody>
            </Card>
        </Container>
    );
};

export default PurchasePage;
